import React, {
	useContext,
	useMemo,
	useCallback,
	useEffect,
	useState,
} from "react";
import _ from "lodash";
import {
	useParams,
	useNavigate,
	createSearchParams,
	useLocation,
} from "react-router-dom";
import styled, { useTheme } from "styled-components";
import * as quoteReqs from "../../requests/quoteManagementRequests";
import ConfiguratorContext from "../../context/configurator/ConfiguratorContext";
import AlertContext from "../../context/alert/AlertContext";
import guid, { GenerateGUID } from "../../modules/guid";
import { Body } from "../UI/Text";
import Table from "../UI/Table";
import Modal from "../UI/Modal";
import Button from "../UI/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import UserContext from "../../context/user/UserContext";
import ProductCatalog from "../ProductCatalog";
import { useQuery } from "../Hooks";
import { getProducts } from "../../requests/productManagementRequests";
import * as constants from "../../modules/constants";
import PagerContextProvider from "../UI/Pager/PagerContext";
import DataImport from "../UI/DataImport";
import PriceDisplay from "./PriceDisplay";
import { Input } from "../UI/forms";
import { Formik, Form } from "formik";
import { Flex, Box } from "reflexbox";
const { createLine, updateLine, deleteLine, copyLineConfiguration } = quoteReqs;

const ButtonsWrapper = styled(Flex)`
	justify-content: center;
	align-items: center;
	transition: 200ms all ease-out;
	height: 100%;
`;

const QuantityWrapper = styled(Input)`
	text-align: center;
`;

const LineImg = styled.img`
	max-width: 75px;
	max-height: 75px;
	width: auto !important;
	height: auto;
`;

const Lines = ({
	refetchQuote,
	quoteLoading,
	isLoading,
	show,
	setShow,
	title,
}) => {
	const location = useLocation();
	const { 1: priceMode } = location.pathname.split("/");
	const { quote, setQuote } = useContext(ConfiguratorContext);
	const { activeUser, pid, isLoading: userLoading } = useContext(UserContext);
	const navigate = useNavigate();
	const theme = useTheme();
	const { showFailureAlert, showSuccessAlert } = useContext(AlertContext);
	const { username } = activeUser || {};
	const configuratorContext = useContext(ConfiguratorContext);
	const { type: quoteType } = quote || {};
	const { quoteId } = useParams();
	const isPlane = useMemo(() => quote?.isPlane, [quote]);
	const isDealer = useMemo(
		() => priceMode === "DealerQuote" || !isPlane,
		[priceMode, isPlane]
	);

	const [importing, setImporting] = useState(false);

	const _lines = useQuery({
		queryID: `lines|${quoteId}`,
		request: quoteReqs.getLines,
		queryVariables: { quoteId, isDealer, erpId: quote?.customer?.erpId },
		skip:
			!quoteId ||
			!quote?.customer?.erpId ||
			isLoading ||
			quoteLoading ||
			userLoading ||
			importing,
	});

	useEffect(() => {
		if (
			_lines?.data &&
			!_lines.isLoading &&
			!quote?.lines &&
			!quote?.lineCount &&
			!isLoading &&
			!importing
		)
			setQuote({
				...quote,
				lines: _lines?.data?.rows,
				lineCount: _lines?.data?.count,
			});
	}, [_lines, isLoading, quote, setQuote, importing]);

	const products = useQuery({
		queryID: "quote-products",
		request: getProducts,
		queryVariables: { pid, gid: quote?.groupId },
		skip: !pid || userLoading,
		attributes: [
			"ProductNumber",
			"Name",
			"ID",
			"ApplicationInstance",
			"ApplicationName",
			"isNonConfigurable",
			"isQuickConfig",
			"isNonConfigurable",
			"HeaderId",
			"img",
			"defaultPrice",
			"Description",
			"HeaderId",
			"Overview",
			"quantityInStock",
			"priceCode",
			"Discount",
			"RelatedItem",
			"PortalPricingEnabled",
		],
		forcedFilters: {},
	});

	const {
		setActiveProduct,
		setIsReconfigure,
		setConfiguratorMode,
		setFromDealer,
	} = configuratorContext;

	const onReconfigure = useCallback(
		async (row) => {
			const { productId, configurationId, id: LineId, headerId } = row;
			setIsReconfigure(true);
			setActiveProduct(productId);
			setConfiguratorMode(constants.CONFIGURATOR_MODES.Default);
			setFromDealer(isDealer);

			const lineNumber =
				_.findIndex(quote?.lines, (line) => line.id === LineId) + 1;
			refetchQuote("quotes");

			let paramsObj = {
				lineNumber,
				LineId,
				headerId,
				configurationId,
			};

			navigate({
				pathname: `/Configurator/${productId}/${quoteId}/${configurationId}`,
				search: createSearchParams(paramsObj).toString(),
			});
		},
		[
			setIsReconfigure,
			setActiveProduct,
			setConfiguratorMode,
			setFromDealer,
			isDealer,
			quote,
			refetchQuote,
			navigate,
			quoteId,
		]
	);

	const onCopyLine = useCallback(
		async (row) => {
			const oldLine = _.find(quote?.lines, ({ _id }) => _id === row._id);
			const {
				configurationId: sourceConfigurationId,
				headerId: sourceHeaderId,
				price,
				quantity,
				productId,
				isNonConfigurable,				
			} = oldLine;
			const newLine = {
				...oldLine,
				isNonConfigurable,
				// Creating a GUID here to allow for the deleting and updating of rows
				// before the query has refetched.
				_id: guid.GenerateGUID(),
			};
			if (!isNonConfigurable) {
				newLine.sourceConfigurationId = sourceConfigurationId;
				newLine.sourceHeaderId = sourceHeaderId;
				// need to update this to syteline format
				newLine.configurationId = `${quoteId}_${quote?.lines?.length + 1}`;
				newLine.headerId = sourceHeaderId;
			}
			const newLines = [...quote?.lines, newLine];
			const newPrice = quote?.totalCost + price * quantity;
			setQuote({ ...quote, totalCost: newPrice, lines: newLines });

			await copyLineConfiguration({
				sourceHeaderId,
				sourceDetailId: sourceConfigurationId,
				headerId: sourceHeaderId,
				detailId: `${quoteId}_${quote?.lines?.length + 1}`,
				productId,
			});

			await createLine({
				input: { ...newLine, isPlane, erpId: quote?.customer?.erpId },
			});
		},
		[quote, setQuote, isPlane, quoteId]
	);

	const onDeleteLine = useCallback(
		async (row) => {
			const { lines } = quote || {};
			const { _id, price = 0, quantity = 1 } = row || {};

			const newLines = [...lines];
			const i = _.findIndex(newLines, (line) => line?._id === _id);
			newLines.splice(i, 1);

			const newPrice = quote?.totalCost - +price * +quantity;

			_lines.setData({
				rows: newLines,
				count: _lines?.data?.count - 1,
			});

			setQuote({
				...quote,
				totalCost: newPrice,
				lines: newLines,
				lineCount: _lines?.data?.count - 1,
			});
			await deleteLine({ _id, quoteId });
			_lines.setData({
				rows: newLines,
				count: _lines?.data?.count - 1,
			});
		},
		[quote, setQuote, quoteId, _lines]
	);

	const onUpdateQuantity = useCallback(
		async (values, row) => {
			const { _id, quantity, price, id } = row || {};
			const newLines = quote?.lines.map((line) =>
				line._id === _id ? { ...line, quantity: values.quantity } : line
			);

			_lines.setData({
				rows: newLines,
				count: _lines?.data?.count,
			});

			const newPrice =
				quote?.totalCost - quantity * price + values.quantity * price;
			setQuote({
				...quote,
				totalCost: newPrice,
				lines: newLines,
			});

			await updateLine({
				id,
				_id,
				input: { quantity: values.quantity, isPlane },
			});
		},
		[quote, _lines, setQuote, isPlane]
	);

	const getSelectedProduct = useCallback(
		(productId) => {
			return _.find(products.data?.rows, (product) => product.ID === productId);
		},
		[products.data]
	);

	const onAddLine = useCallback(
		async (productId, quantity = 1) => {
			setIsReconfigure(false);
			setConfiguratorMode(constants.CONFIGURATOR_MODES.Default);

			const selectedProduct = getSelectedProduct(productId);

			if (isPlane && selectedProduct?.isNonConfigurable === true)
				return showFailureAlert(`You cannot add parts lines to a Plane quote.`);
			else if (!isPlane && selectedProduct?.isNonConfigurable === false)
				return showFailureAlert(`You cannot add a plane to a parts order.`);

			const { lines = [] } = quote || {};

			const { isNonConfigurable, isQuickConfig } = selectedProduct || {};
			if (isNonConfigurable) {
				const index = _.findIndex(
					lines,
					(line) => line.productId === productId
				);

				const newLines = [...lines];
				if (index !== -1) {
					const { quantity: _qty } = newLines[index] || {};
					const newQty = _qty + quantity;

					newLines[index].quantity = newQty;

					const newPrice =
						quote?.totalCost -
						selectedProduct?.defaultPrice * _qty +
						selectedProduct?.defaultPrice * newQty;
					setQuote({
						...quote,
						totalCost: newPrice,
						lines: newLines,
					});
					await updateLine({
						input: { quantity: newQty },
						_id: newLines[index]._id,
					});
				} else {
					const dealerPrice = (
						selectedProduct?.defaultPrice *
						((selectedProduct?.discountPercentage * -1) / 100)
					).toFixed(2);

					const newLine = {
						_id: GenerateGUID(),
						headerId: selectedProduct?.HeaderId,
						imgUrl: selectedProduct?.img,
						price: selectedProduct?.defaultPrice,
						username: username,
						partNumber: selectedProduct?.ProductNumber,
						description: selectedProduct?.Description,
						discount_percentage: selectedProduct?.discountPercentage,
						productId,
						quoteId,
						dealerPrice: dealerPrice,
						quantity,
					};
					newLines.push(newLine);

					const newPrice =
						quote?.totalCost + selectedProduct?.defaultPrice * quantity;
					setQuote({
						...quote,
						totalCost: newPrice,
						lines: newLines,
						lineCount: newLines.length,
					});
					showSuccessAlert(
						`Added ${selectedProduct?.ProductNumber} to Quote #${quoteId}`
					);
					_lines.setData({
						rows: newLines,
						count: newLines.length,
					});
					await createLine({
						input: {
							...newLines[newLines.length - 1],
							isPlane,
							erpId: quote?.customer?.erpId,
						},
					});
				}

				return;
			} else {
				let lineNumber = 1;

				if (lines && lines.length > 0) {
					lineNumber = lines.length + 1;
				}

				const configurationId = `${quoteId}_${lineNumber}`;
				setFromDealer(isDealer);
				if (!isNonConfigurable || isQuickConfig)
					navigate({
						pathname: `/Configurator/${productId}/${quoteId}/${configurationId}`,
						search: createSearchParams({
							lineNumber,
						}).toString(),
					});
			}
		},
		[
			setFromDealer,
			isDealer,
			setIsReconfigure,
			setConfiguratorMode,
			getSelectedProduct,
			isPlane,
			showFailureAlert,
			quote,
			username,
			quoteId,
			setQuote,
			_lines,
			showSuccessAlert,
			navigate,
		]
	);

	const columns = useMemo(
		() => [
			{
				id: "imgUrl",
				data: (row) => (
					<Flex
						width={1}
						alignItems="center"
						height="100%"
						key={row?.id + "|imgUrl"}
						justifyContent="center"
					>
						{(row?.imgUrl?.startsWith("http") ||
							row?.imgUrl?.startsWith("/") ||
							row?.base64Image) && (
							<LineImg
								src={row?.base64Image ? row.base64Image : row?.imgUrl}
								alt={row?.id || "imgUrl"}
							/>
						)}
					</Flex>
				),
				header: "Preview",
				size: { md: 2, sm: -1, xs: -1 },
			},
			{
				id: "partNumber",
				data: (row) => (
					<Body
						key={row?.partNumber}
						color={row?.id === "new" ? theme.secondary : undefined}
					>
						{row?.partNumber}
					</Body>
				),
				sort: false,
				header: "Part #",
				size: { md: 2, sm: 2, xs: 3 },
			},
			{
				id: "description",
				data: (row) => (
					<Flex
						height="100%"
						justifyContent="center"
						alignItems="center"
						flexWrap="wrap"
						key={row?.id + "|description"}
					>
						<Body>{row?.description}</Body>
					</Flex>
				),
				header: "Description",
				size: { md: 4, sm: 3, xs: -1 },
			},
			{
				id: "headerId",
				data: (row) => (
					<Flex
						height="100%"
						justifyContent="center"
						alignItems="center"
						flexWrap="wrap"
						key={row?.id + "|headerId"}
					>
						<Body>{row?.headerId}</Body>
					</Flex>
				),
				header: "Header ID",
				size: { md: -1, sm: -1, xs: -1 },
			},
			{
				id: "quantity",
				data: (row) => {
					return quoteType === 2 ? (
						<Body center>{row?.quantity}</Body>
					) : (
						<Flex
							height="100%"
							justifyContent="center"
							alignItems="center"
							key={row?.id + "|quantity"}
						>
							<ButtonsWrapper id="actions" m="0">
								<Button
									iconName="faMinus"
									reverse
									size="tiny"
									color={theme.secondary}
									onClick={(e) => {
										e.preventDefault();
										return row?.quantity > 1
											? onUpdateQuantity({ quantity: row?.quantity - 1 }, row)
											: showFailureAlert(
													"Cannot set quantity to a number less than 1."
											  );
									}}
								/>
							</ButtonsWrapper>
							<Formik
								enableReinitialize
								initialValues={{
									quantity: row?.quantity,
									row: row,
								}}
								validateOnMount={false}
								validateOnChange={false}
							>
								{({ values }) => {
									return (
										<Form
											style={{
												width: "45%",
												display: "inherit",
												textAlign: "right",
												minWidth: "50px",
											}}
										>
											<QuantityWrapper
												type="text"
												onBlur={(e) => {
													if (isNaN(e.target.value)) return;

													if (Number(e.target.value) <= 0) {
														showFailureAlert(
															"Quantity must be greater than zero."
														);
														return;
													}
													onUpdateQuantity(
														{ quantity: Number(e.target.value) },
														values.row
													);
												}}
												name="quantity"
												value={values.quantity}
											/>
										</Form>
									);
								}}
							</Formik>

							{/* <Body mx="0" bold center>
                {row?.quantity}
              </Body> */}
							<ButtonsWrapper id="actions" m="0">
								<Button
									iconName="faPlus"
									reverse
									size="tiny"
									color={theme.secondary}
									onClick={(e) => {
										e.preventDefault();
										onUpdateQuantity({ quantity: row?.quantity + 1 }, row);
									}}
								/>
							</ButtonsWrapper>
						</Flex>
					);
				},
				header: "Quantity",
				size: { md: 3, sm: 2, xs: 5 },
			},
			{
				id: "price",
				data: (row) => (
					<PriceDisplay
						isDealer={isDealer}
						isPlane={isPlane}
						isUnit
						row={row}
					/>
				),

				header: "Unit Price",
				size: { md: 2.5, sm: -1, xs: -1 },
				sort: false,
			},
			{
				id: "totalPrice",
				data: (row) => (
					<PriceDisplay isDealer={isDealer} isPlane={isPlane} row={row} />
				),
				header: "Total",
				size: { md: 2.5, sm: 2, xs: 2 },
				sort: false,
			},
			{
				id: "actions",
				header: "",
				sort: false,
				data: (row) => {
					return (
						<ButtonsWrapper
							id="actions"
							m="0 auto"
							height="100%"
							key={row?.id + "|actions"}
						>
							{!isPlane ? null : (
								<>
									<Button
										size="small"
										ml="1rem"
										color={theme.secondary}
										reverse
										iconName="faPencil"
										title="Edit Configuration"
										onClick={() => onReconfigure(row)}
									/>
									<Button
										size="small"
										color={theme.success}
										reverse
										iconName="faCopy"
										title="Copy Line"
										onClick={() => onCopyLine(row)}
									/>
								</>
							)}

							<Button
								size="small"
								color={theme.danger}
								reverse
								title="Delete Line"
								iconName="faTrashCan"
								onClick={() => onDeleteLine(row)}
							/>
						</ButtonsWrapper>
					);
				},
				size:
					quoteType === 2 && activeUser?.roleLevel < 5
						? { md: -1, sm: -1, xs: -1 }
						: { lg: 4, md: 0, sm: 0, xs: 0 },
			},
		],
		[
			isDealer,
			isPlane,
			onCopyLine,
			onDeleteLine,
			onReconfigure,
			onUpdateQuantity,
			quoteType,
			showFailureAlert,
			theme.danger,
			theme.secondary,
			theme.success,
			activeUser,
		]
	);

	const runImportLines = useCallback(
		async (file) => {
			setImporting(true);
			const [cols, ...itemsToImport] = file.split("\r\n");
			const newLines = [];
			let failedRows = [];

			if (!products) {
				const error = `Failed to pull products for group.`;
				showFailureAlert(error);
				return;
			}

			for (let i in itemsToImport) {
				const item = itemsToImport[i];
				const [itemCol1, itemCol2] = item.split(",") || [];

				const productRows = products?.data?.rows;

				const product = _.find(productRows, function (ProductNumber) {
					if (ProductNumber.Name.toLowerCase() === itemCol1.toLowerCase())
						return true;
				});

				if (!product) {
					if (itemCol1 === "") {
						if (itemCol2) {
							const error = `Failed to import Line for: ROW - ${
								parseInt(i) + 1
							}: Invalid part.`;
							showFailureAlert(error);
							failedRows.push(error);
							continue;
						} else {
							continue;
						}
					} else if (isNaN(itemCol2)) {
						const error = `Failed to import Line for ${itemCol1}: ROW - ${
							parseInt(i) + 1
						}: Quantity is not a number.`;
						failedRows.push(error);
						showFailureAlert(error);
						continue;
					} else if (parseInt(itemCol2) <= 0 || !itemCol2) {
						const error = `Failed to import Line for ${itemCol1}: ROW - ${
							parseInt(i) + 1
						}: No quantity set.`;
						failedRows.push(error);
						showFailureAlert(error);
						continue;
					} else {
						const error = `Failed to import Line for: ROW - ${
							parseInt(i) + 1
						}: Invalid part.`;
						showFailureAlert(error);
						failedRows.push(error);
						continue;
					}
				} else if (isNaN(itemCol2)) {
					const error = `Failed to import Line for ${itemCol1}: ROW - ${
						parseInt(i) + 1
					}: Quantity is not a number.`;
					failedRows.push(error);
					showFailureAlert(error);
					continue;
				} else if (parseInt(itemCol2) <= 0 || !itemCol2) {
					const error = `Failed to import Line for ${itemCol1}: ROW - ${
						parseInt(i) + 1
					}: No quantity set.`;
					failedRows.push(error);
					showFailureAlert(error);
					continue;
				} else if (product?.PortalPricingEnabled === false) {
					const error = `Failed to import Line for ${itemCol1}: ROW - ${
						parseInt(i) + 1
					}: Portal Pricing not enabled.`;
					failedRows.push(error);
					showFailureAlert(error);
					continue;
				}

				const newLine = {
					quantity: itemCol2,
					quoteId,
					_id: GenerateGUID(),
					productId: product?.ID,
					headerId: product?.HeaderId,
					imgUrl: product?.img,
					price: product?.defaultPrice,
					username: username,
					partNumber: product?.ProductNumber,
					description: product?.Description,
				};

				try {
					const line = _.find(
						quote?.lines,
						({ partNumber }) =>
							partNumber.toLowerCase() === itemCol1.toLowerCase()
					);

					if (line) {
						const { id, quantity } = line;

						await updateLine({
							id,
							input: { quantity: quantity + +itemCol2 },
						});
						newLines.push({ ...line, quantity: quantity + +itemCol2 });

						showSuccessAlert(`Successfully imported new line for ${itemCol1}`);
					} else {
						if (itemCol1 && itemCol2 && product) {
							const line = await createLine({
								input: { ...newLine, isPlane, erpId: quote?.customer?.erpId },
							});
							newLines.push(line);
							showSuccessAlert(
								`Successfully imported new line for ${itemCol1}`
							);
						}
					}
				} catch (error) {
					console.error(error);
					const importError = `Failed to import Line for ${itemCol1}: ROW - ${
						parseInt(i) + 1
					}: ${error}`;
					failedRows.push(importError);
					showFailureAlert(importError);
				}
			}

			if (failedRows.length > 0) {
				await quoteReqs.downloadFailedImportCSV(failedRows);
				window.location.reload();
			}

			setQuote({ ...quote, lines: newLines });
			setImporting(false);
			_lines.refetchQuery();
		},
		[
			products,
			setQuote,
			quote,
			_lines,
			showFailureAlert,
			quoteId,
			username,
			showSuccessAlert,
			isPlane,
		]
	);

	return (
		<>
			<Modal show={show} setShow={setShow}>
				<PagerContextProvider>
					<ProductCatalog
						onProductClick={onAddLine}
						isLoading={isLoading || products?.isLoading}
					/>
				</PagerContextProvider>
			</Modal>
			<PagerContextProvider>
				<Table
					title={title}
					additionalElements={[
						<ProductCatalog
							key="product-catalog"
							onProductClick={onAddLine}
							inline
						/>,
					]}
					hideSearch
					hideBack
					hideProperties
					hidePager
					hideCreate={isPlane === true}
					createButton={
						isPlane === true ? (
							<Button style={{ opacity: 0, pointerEvents: "none" }}>
								Import
							</Button>
						) : (
							<DataImport key="data-import" onSubmit={runImportLines} />
						)
					}
					data={quote?.lines || []}
					columns={columns}
					totalCount={quote?.lineCount}
					isLoading={importing || _lines.isLoading}
				/>
			</PagerContextProvider>
		</>
	);
};

export default Lines;
